import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldAlert, RefreshCw, Clock, User, FileText, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/seo/audit-logs`, { withCredentials: true });
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    if (action.includes('CREATE')) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (action.includes('DELETE')) return 'bg-red-500/10 text-red-400 border-red-500/20';
    if (action.includes('UPDATE')) return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            SEO Audit & Change Logs
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
            An immutable record of who changed what in the SEO settings. Used for accountability and rollback reference.
          </p>
        </div>
        <Button onClick={fetchLogs} disabled={loading} variant="outline" size="sm" className="bg-white dark:bg-[#131B2C] text-gray-900 dark:text-white border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:bg-white/5 rounded-xl">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-white/[0.02] border-b border-gray-100 dark:border-white/[0.05]">
              <tr>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Timestamp</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Admin ID</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Action</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Entity</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {logs.map((log) => (
                <tr key={log._id} className="hover:bg-gray-50 dark:bg-white/[0.01]">
                  <td className="p-4 text-gray-500 dark:text-[#94A3B8] font-mono text-xs whitespace-nowrap flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    {log.timestamp ? formatDistanceToNow(new Date(log.timestamp), { addSuffix: true }) : 'Unknown'}
                  </td>
                  <td className="p-4 text-gray-900 dark:text-white font-mono text-xs flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-zinc-500" />
                    {log.user_id}
                  </td>
                  <td className="p-4">
                    <Badge variant="outline" className={`text-[10px] uppercase font-bold tracking-wider ${getActionColor(log.action)}`}>
                      {log.action.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="p-4 text-gray-900 dark:text-white font-medium text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    {log.entity}
                  </td>
                  <td className="p-4 text-gray-500 dark:text-[#94A3B8] text-xs max-w-[200px] truncate" title={JSON.stringify(log.details)}>
                    {log.details && Object.keys(log.details).length > 0 ? (
                      <span className="font-mono bg-white/[0.05] px-1.5 py-0.5 rounded text-[10px]">
                        {JSON.stringify(log.details)}
                      </span>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500 dark:text-[#94A3B8]">
                    No audit logs found. Make a change in the SEO manager to see it tracked here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
